/**
 * PDP visual configuration picker + configurator deep link.
 *
 * <gk-variant-cards> only decorates: the radios inside it are read by the
 * parent <variant-radios> (global.js), which still owns variant id, URL,
 * price, media and add-to-cart state. Here we keep the selected card in view
 * on the mobile rail, refresh card price/image for multi-option products, and
 * crossfade the gallery image Dawn just activated.
 *
 * <gk-config-link> swaps the configurator href/copy for the selected variant.
 * It listens at document level so it runs after VariantSelects has resolved
 * `currentVariant` on its own element-level change listener.
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function readJson(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function replay(element, className) {
    if (!element || reducedMotion.matches) return;
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
    element.addEventListener(
      'animationend',
      function () {
        element.classList.remove(className);
      },
      { once: true }
    );
  }

  if (!customElements.get('gk-variant-cards')) {
    class GkVariantCards extends HTMLElement {
      connectedCallback() {
        if (this.handleChange) return;
        this.picker = this.closest('variant-radios');
        this.list = this.querySelector('.gk-vcards__list');
        this.optionIndex = Number(this.dataset.optionIndex) || 0;
        this.variants = readJson(this, '[data-gk-variants]');
        this.handleChange = this.onChange.bind(this);
        (this.picker || this).addEventListener('change', this.handleChange);
        window.requestAnimationFrame(this.revealSelected.bind(this, false));
      }

      disconnectedCallback() {
        if (!this.handleChange) return;
        (this.picker || this).removeEventListener('change', this.handleChange);
        this.handleChange = null;
      }

      onChange(event) {
        if (!event.target || event.target.type !== 'radio') return;
        if (this.variants) this.refreshCards();
        if (this.contains(event.target)) this.revealSelected(true);
        window.requestAnimationFrame(this.fadeActiveMedia.bind(this));
      }

      selectedOptions() {
        var root = this.picker || this;
        return Array.prototype.map.call(root.querySelectorAll('fieldset'), function (fieldset) {
          var checked = fieldset.querySelector('input:checked');
          return checked ? checked.value : null;
        });
      }

      refreshCards() {
        var selected = this.selectedOptions();
        var index = this.optionIndex;
        var variants = this.variants;
        this.querySelectorAll('.gk-vcard').forEach(function (card) {
          var value = card.dataset.value;
          var match = null;
          for (var i = 0; i < variants.length; i += 1) {
            var options = variants[i].options;
            var ok = true;
            for (var j = 0; j < options.length; j += 1) {
              var wanted = j === index ? value : selected[j];
              if (options[j] !== wanted) {
                ok = false;
                break;
              }
            }
            if (ok) {
              match = variants[i];
              break;
            }
          }

          card.classList.toggle('is-soldout', !match || !match.available);
          var flag = card.querySelector('[data-gk-card-flag]');
          if (flag) flag.textContent = !match ? 'Unavailable' : match.available ? '' : 'Sold out';

          var price = card.querySelector('[data-gk-card-price]');
          if (price) {
            price.textContent = '';
            if (match) {
              var now = document.createElement('span');
              now.className = 'gk-vcard__price-now';
              now.textContent = match.price;
              price.appendChild(now);
              if (match.compare) {
                var was = document.createElement('s');
                was.className = 'gk-vcard__price-was';
                was.textContent = match.compare;
                price.appendChild(was);
              }
            }
          }

          var img = card.querySelector('.gk-vcard__img');
          if (img && match && match.image && img.getAttribute('src') !== match.image) {
            img.removeAttribute('srcset');
            img.setAttribute('src', match.image);
            replay(img, 'gk-fade-in');
          }
        });
      }

      revealSelected(animate) {
        if (!this.list || this.list.scrollWidth <= this.list.clientWidth + 1) return;
        var checked = this.list.querySelector('input:checked');
        var card = checked && checked.closest('.gk-vcard');
        if (!card) return;
        var listBox = this.list.getBoundingClientRect();
        var cardBox = card.getBoundingClientRect();
        var overflowLeft = cardBox.left - listBox.left;
        var overflowRight = cardBox.right - listBox.right;
        var delta = overflowLeft < 0 ? overflowLeft : overflowRight > 0 ? overflowRight : 0;
        if (!delta) return;
        this.list.scrollTo({
          left: this.list.scrollLeft + delta,
          behavior: animate && !reducedMotion.matches ? 'smooth' : 'auto',
        });
      }

      fadeActiveMedia() {
        var section = this.dataset.section;
        var gallery = section && document.getElementById('MediaGallery-' + section);
        if (!gallery) return;
        replay(gallery.querySelector('.product__media-item.is-active'), 'gk-media-swap');
      }
    }

    customElements.define('gk-variant-cards', GkVariantCards);
  }

  if (!customElements.get('gk-config-link')) {
    class GkConfigLink extends HTMLElement {
      connectedCallback() {
        if (this.handleChange) return;
        this.map = readJson(this, '[data-gk-cfg-map]') || {};
        this.anchor = this.querySelector('[data-gk-cfg-anchor]');
        this.titleEl = this.querySelector('[data-gk-cfg-title]');
        this.noteEl = this.querySelector('[data-gk-cfg-note]');
        this.handleChange = this.onChange.bind(this);
        document.addEventListener('change', this.handleChange);
      }

      disconnectedCallback() {
        if (!this.handleChange) return;
        document.removeEventListener('change', this.handleChange);
        this.handleChange = null;
      }

      onChange(event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var picker = target.closest('variant-radios, variant-selects');
        if (!picker || picker.dataset.section !== this.dataset.section) return;
        var variant = picker.currentVariant;
        var entry = variant && this.map[String(variant.id)];
        if (!entry || !this.anchor) return;
        if (this.anchor.getAttribute('href') === entry.href) return;
        this.anchor.setAttribute('href', entry.href);
        if (this.titleEl) this.titleEl.textContent = entry.title;
        if (this.noteEl) this.noteEl.textContent = entry.note;
        this.dataset.exact = String(entry.exact);
        replay(this, 'gk-cfg--updated');
      }
    }

    customElements.define('gk-config-link', GkConfigLink);
  }
})();
